<script setup>
import useSystemFields from '@convenia/employee-organisms/SystemFields/composables/useSystemFields'
import { computed, onMounted, ref, inject } from 'vue'
import { mapCustomDocument, documentMappers as mappers } from '../content/mappers'

import DocumentUpload from './DocumentUpload'

const props = defineProps({
  value: {
    type: Object,
    default: () => ({
      title: '',
      text: '',
      data: {},
      files: [],
      filesPlaceholder: [],
      filesLimit: 2,
      filesSlots: true,
    })
  },

  type: {
    type: String,
    default: null
  },

  schema: {
    type: Object,
    default: () => ({})
  },

  options: {
    type: Object,
    default: () => ({})
  },

  systemFields: {
    type: Object,
    default: () => ({})
  },

  customFields: {
    type: Object,
    default: () => ({})
  },

  title: {
    type: String,
    default: 'Documento'
  },

  text: {
    type: String,
    default: undefined
  },

  loading: {
    type: Boolean,
    default: false
  },

  disabled: {
    type: Boolean,
    default: false
  },

  theme: {
    type: String,
    default: 'primary' // primary | accent
  },

  edit: Boolean,

  readOnly: Boolean,
})

const emit = defineEmits([ 'edit', 'close', 'submit' ])

const employeeId = inject('employeeId')
const getFileUrl = inject('getFileUrl')

const customFields = computed(() => Object.entries(props.customFields || {})
  .reduce((acc, [ key, field ]) => ({
    ...acc,
    ...([ 'attachment' ].includes(field.type)
      ? {}
      : { [key]: field }
    )
  }), {})
)

const {
  formSchema,
  getFormValues,
  requiredFields,
} = useSystemFields({
  schema: computed(() => props.schema),
  systemFields: computed(() => props.systemFields),
  customFields,
})

const conditionalRequiredFields = computed(() => {
  if (props.value?.isOptional)
    return []

  return requiredFields.value
})

const data = ref({})
const replaced = ref([])
const files = ref([])
const upload = ref(null)

const requiredFiles = computed(() => {
  if (props.value?.isOptional)
    return false

  const attachmentFields = [
    props.systemFields?.attachment,
    ...Object.values(props.customFields || {})
      .filter(field => field.type === 'attachment')
  ]

  return attachmentFields.some(field => field?.mandatory || field?.compulsory)
})

const createInitialFiles = () => {
  const files = data.value.files || []
  const placeholders = props.value.filesPlaceholder || []

  const filesSize = props.value.filesSlots
    ? Math.max(files.length, placeholders.length, 1)
    : Math.max(files.length, 1)

  return Array.from({ length: filesSize }, (_, i) => ({
    ...(files[i] || {}),
    placeholder: placeholders[i]
  }))
}

const showAddButton = computed(() => {
  const currentFiles = files.value.filter(file => !!file.name)

  return !props.value.filesSlots && currentFiles.length > 0
})

const hasAttachment = computed(() =>
  Object.keys(props.systemFields || {}).includes('attachment')
  || Object.values(props.customFields || {}).findIndex(field => field.type === 'attachment') >= 0
)

const hasFields = computed(() => (
  Object.values(formSchema.value || {})
    .some(field => !field.hide)
))

const onAddFile = ({ file, index }) => {
  const removed = files.value.splice(index, 1, file)
  const replacedFiles = removed.filter(({ id }) => !!id)

  replaced.value = [ ...replaced.value, ...replacedFiles ]
}

const onRemoveFile = (index) => {
  if (files.value[index]?.id)
    replaced.value.push(files.value[index])

  if (!props.value.filesSlots && files.value.length > 1)
    return files.value.splice(index, 1)

  files.value = files.value.map((file, i) => (
    index === i ? { placeholder: file.placeholder } : file
  ))
}

const getValues = () => {
  const mapper = mappers[props.type] ?? mapCustomDocument
  const document = mapper(props.value?.data || {}, { employeeId, getFileUrl })

  return getFormValues(document || {}, props.value?.customFieldValues || {})
}

const onSubmit = (formData) => {
  if (requiredFiles.value && !upload.value.validate())
    return

  emit('submit', {
    data: formData,
    replaced: replaced.value,
    files: files.value
  })
}

onMounted(() => {
  data.value = getValues()
  files.value = createInitialFiles()
})
</script>

<template>
  <c-form-builder-modal
    label-left
    class="document-form-modal"
    name="document-modal"
    alternative-header
    header-icon="user-profile-8"
    v-model="data"
    has-files
    :header-theme="theme"
    :title="value.title"
    :files="files"
    :schema="formSchema"
    :fields-options="options"
    :is-disabled="disabled || loading"
    :is-loading="loading"
    :has-top-shadow="false"
    :has-bottom-shadow="false"
    :required-fields="conditionalRequiredFields"
    :read-only="readOnly"
    :show-edit-button="edit"
    @submit="onSubmit"
    @edit="emit('edit')"
    @close="emit('close')"
  >
    <div
      class="upload"
      footer
      v-if="hasAttachment"
    >
      <c-title v-if="hasFields" dark-text class="title">
        Anexos
      </c-title>

      <span
        v-if="value.text"
        class="text"
        v-html="value.text"
      />

      <document-upload
        ref="upload"
        :value="files"
        :add="showAddButton"
        :disabled="disabled"
        :required="requiredFiles"
        :slots="value.filesSlots"
        :limit="value.filesLimit"
        :style="{ marginTop: hasFields ? '0px' : '20px' }"
        @add="onAddFile"
        @remove="onRemoveFile"
      />
    </div>
  </c-form-builder-modal>
</template>

<style lang="scss">
.document-form-modal {
  & > .wrapper .modal {
    & > .header {
      display: flex;
      align-items: center;
    }
    & > .content {
      .upload {
        & > .title {
          margin-bottom: 20px;
        }

        & > .text {
          font-size: 14px;
          line-height: 19px;
          color: var(--color-text-base-80);
          display: block;
          margin-bottom: 20px;
          font-weight: 500;
          & > b { font-weight: 600; }
        }
      }

      & > .body {
        & > .form {
          & > .c-loader {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }

          & > .c-form-builder {
            & > .actions { display: none }
          }
        }
      }

      & > .actions {
        display: flex;
        justify-content: center;
        margin-top: 40px;
        & > .c-button { width: 180px; margin: 0 10px; }
      }
    }
  }

  @include responsive (xs-mobile, mobile) {
    & > .wrapper { overflow: hidden; }

    & > .wrapper > .modal {
      & > .header { padding: 10px; }

      & > .content {
        flex: 1;
        padding: { left: 0; right: 0; bottom: 0; }

        & > .submit {
          width: 100%;
          border-radius: 0 !important;
          min-height: 60px !important;
          position: absolute;
          bottom: 0;
          left: 0;
        }
      }
    }
  }

  @include responsive (tablet, desktop) {
    & > .wrapper > .modal {
      min-width: 960px;

      & > .content {
        & > .body {
          & > .form {
            & > .c-form-builder {
              & > .c-shadowed {
                overflow: initial;
                & > .wrapper {
                  overflow: initial;
                  max-width: initial;

                  & > .form-fields { padding: 0; }
                }
              }
            }
          }

          & > .actions {
            display: flex;
            margin-top: 40px;
            justify-content: flex-end;

            .c-button {
              margin: 0 10px; width: 180px;

              &.-error > .text { color: #FF7EB3; }
            }
          }
        }

        .upload { max-width: 590px; margin-left: -20px; }
      }
    }
  }
}
</style>
